import { InfoOutlined } from '@mui/icons-material';
import { Box, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { Button, LocalModal } from '@/components';
import { Routes } from '@/constants';
import { Link } from '@/containers';
import { CancelStripeSubscriptionPayload } from '@/requests';
import { AnalyticsService, EVENTS } from '@/services';
import {
  RefetchOnMountDelay,
  useAddRecoveryEmailMutation,
  useCancelStripeSubscriptionMutation,
  useFetchPlanQuery,
  useFetchRecoveryEmailQuery,
  useFetchUserProfileQuery,
  useFetchUserStripeCustomerInfoQuery,
  useGenerateBillingPortalLinkMutation,
  useRemoveRecoveryEmailMutation,
  useVerifyRecoveryEmailMutation,
} from '@/store';
import { SnackbarUtils } from '@/utils';

import { AddRecoveryEmail } from './AddRecoveryEmail';
import { CancelStripeSubscription } from './CancelStripeSubscription';
import { EditEmail } from './EditEmail';
import { EditPassword } from './EditPassword';

export const AccountDetails = () => {
  const navigate = useNavigate();

  const { data: user } = useFetchUserProfileQuery();
  const { data: recoveryEmail } = useFetchRecoveryEmailQuery();
  const [addRecoveryEmail] = useAddRecoveryEmailMutation();
  const [removeRecoveryEmail] = useRemoveRecoveryEmailMutation();
  const [isAddRecoveryDialogOpen, setIsAddRecoveryDialogOpen] = useState(false);
  const [isEditPasswordDialogOpen, setIsEditPasswordDialogOpen] =
    useState(false);
  const [isEditEmailDialogOpen, setIsEditEmailDialogOpen] = useState(false);
  const [isCancelSubDialogOpen, setIsCancelSubDialogOpen] = useState(false);
  const [verifyRecoveryEmail] = useVerifyRecoveryEmailMutation();
  const [cancelStripeSubscription] = useCancelStripeSubscriptionMutation();

  const { data: plan } = useFetchPlanQuery(undefined, {
    refetchOnMountOrArgChange: RefetchOnMountDelay,
    refetchOnFocus: true,
  });

  const { userHasBillingPortalLink, userHasDirectCancelButton } =
    useFetchUserStripeCustomerInfoQuery(undefined, {
      selectFromResult: result => ({
        ...result,
        userHasBillingPortalLink: result.data?.hasBillingPortalAccess,
        userHasDirectCancelButton: result.data?.hasDirectCancelButton,
      }),
    });

  const [
    generateBillingPortalLink,
    { isLoading: isBillingPortalLinkGenerating },
  ] = useGenerateBillingPortalLinkMutation();

  const onViewBillingPortal = async () => {
    try {
      const { url } = await generateBillingPortalLink().unwrap();
      window.location.href = url;
    } catch {
      SnackbarUtils.error(
        'An error occurred while generating the billing portal link. Please try again later.',
      );
    }
  };

  const location = useLocation();

  const showEmailPrompt =
    recoveryEmail?.verificationStatus === 'not_attempted' ||
    recoveryEmail?.verificationStatus === 'unverified';

  const { verification_code: verificationCode } = Object.fromEntries(
    new URLSearchParams(location.search).entries(),
  );

  useEffect(() => {
    if (!verificationCode) {
      return;
    }

    const verifyRecoveryEmailFn = async () => {
      try {
        AnalyticsService.logEvent(EVENTS.AUTH_EVENTS.EMAIL_VALIDATION.INTENT, {
          type: 'recovery email',
        });
        await verifyRecoveryEmail({
          verificationCode,
        }).unwrap();
        AnalyticsService.logEvent(EVENTS.AUTH_EVENTS.EMAIL_VALIDATION.SUCCESS, {
          type: 'recovery email',
        });
        SnackbarUtils.success('Recovery email added successfully.');
      } catch (error) {
        const { data } = error as { data: { message: string } };
        if (data?.message && typeof data?.message === 'string') {
          SnackbarUtils.error(data.message);
        } else {
          SnackbarUtils.error(
            'Something went wrong verifying your recovery email. Please try again or contact support.',
          );
        }
        AnalyticsService.logEvent(EVENTS.AUTH_EVENTS.EMAIL_VALIDATION.FAIL, {
          type: 'recovery email',
          reason: data?.message || '',
        });
      }
    };

    verifyRecoveryEmailFn();
  }, [verificationCode, verifyRecoveryEmail]);

  const onCancelSubscription = async ({
    comment,
    feedback,
  }: CancelStripeSubscriptionPayload) => {
    try {
      await cancelStripeSubscription({
        comment,
        feedback,
      }).unwrap();
      SnackbarUtils.success('Subscription cancelled successfully.');
      setIsCancelSubDialogOpen(false);
    } catch {
      SnackbarUtils.error(
        'Something went wrong. Please try again or contact support.',
      );
    }
  };

  return (
    <Box>
      <Typography variant="h3" sx={{ mb: 1.5 }}>
        Account Details
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <Box sx={styles.container}>
          <Box sx={{ maxWidth: '80%' }}>
            <Typography variant="body2SemiBold" sx={{ mt: 1 }}>
              Email
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              {user?.email}
            </Typography>
          </Box>
          <Button
            variant="secondary"
            label="Edit"
            fullWidth={false}
            sx={styles.button}
            onClick={() => {
              setIsEditEmailDialogOpen(true);
            }}
          />
        </Box>
        <Box sx={styles.container}>
          <Box sx={{ maxWidth: '80%' }}>
            <Typography variant="body2SemiBold" sx={{ mt: 1 }}>
              Password
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              ••••••••
            </Typography>
          </Box>
          <Button
            variant="secondary"
            label="Edit"
            fullWidth={false}
            sx={styles.button}
            onClick={() => {
              setIsEditPasswordDialogOpen(true);
            }}
          />
        </Box>
        <Box sx={styles.container}>
          <Box
            sx={{
              mt: 1,
              maxWidth: '80%',
              gap: 1,
              display: 'flex',
              flexDirection: 'column',
            }}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Typography variant="body2SemiBold">Recovery Email</Typography>
              {showEmailPrompt && (
                <InfoOutlined
                  sx={{
                    color: 'error.main',
                    alignSelf: 'center',
                    fontSize: 18,
                  }}
                />
              )}
            </Box>
            <Typography
              variant="body2"
              sx={{
                ...(recoveryEmail?.recoveryEmail
                  ? {}
                  : { color: 'text.light' }),
              }}>
              {recoveryEmail?.recoveryEmail ||
                'Stay connected to your account and certificates, even if you lose access to your primary email.'}
            </Typography>
            {recoveryEmail?.verificationStatus === 'unverified' && (
              <Typography variant="body2" sx={{ color: 'text.light' }}>
                (Pending Confirmation)&nbsp;
                <Link
                  variant="classic"
                  inline
                  onClick={() => {
                    if (!recoveryEmail?.recoveryEmail) {
                      SnackbarUtils.error('No recovery email set');
                      return;
                    }
                    addRecoveryEmail({
                      recoveryEmail: recoveryEmail.recoveryEmail,
                    });
                    SnackbarUtils.success(
                      'Recovery email resent successfully. Please check your inbox.',
                    );
                  }}>
                  Resend Email
                </Link>
              </Typography>
            )}
          </Box>
          {recoveryEmail?.verificationStatus === 'verified' && (
            <Button
              variant="secondary"
              label="Remove"
              fullWidth={false}
              sx={styles.button}
              onClick={async () => {
                try {
                  await removeRecoveryEmail().unwrap();
                  SnackbarUtils.success('Recovery email removed successfully.');
                } catch (error) {
                  const { data } = error as { data: { message: string } };
                  if (data?.message && typeof data?.message === 'string') {
                    SnackbarUtils.error(data.message);
                  } else {
                    SnackbarUtils.error(
                      'Something went wrong removing your recovery email. Please try again or contact support.',
                    );
                  }
                }
              }}
            />
          )}
          {recoveryEmail?.verificationStatus !== 'verified' && (
            <Button
              variant="secondary"
              label={recoveryEmail?.recoveryEmail ? 'Edit' : 'Add'}
              fullWidth={false}
              sx={styles.button}
              onClick={() => {
                setIsAddRecoveryDialogOpen(true);
              }}
            />
          )}
        </Box>
        {userHasBillingPortalLink && (
          <Box sx={styles.container}>
            <Box
              sx={{
                mt: 1,
                maxWidth: '80%',
                gap: 1,
                display: 'flex',
                flexDirection: 'column',
              }}>
              <Typography variant="body2SemiBold">Billing Details</Typography>
              <Typography variant="body2" sx={{ color: 'text.light' }}>
                Manage your Personal subscription and billing details.
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Button
                variant="secondary"
                label="View Billing Details"
                fullWidth={false}
                isLoading={isBillingPortalLinkGenerating}
                onClick={onViewBillingPortal}
                sx={{ mt: 1 }}
              />
              {userHasDirectCancelButton && (
                <Button
                  variant="failure"
                  label="Cancel Subscription"
                  fullWidth={false}
                  isLoading={isBillingPortalLinkGenerating}
                  onClick={() => setIsCancelSubDialogOpen(true)}
                  sx={{ mt: 2 }}
                />
              )}
            </Box>
          </Box>
        )}
        <Box sx={styles.container}>
          <Box sx={{ maxWidth: '80%', mt: 1 }}>
            <Typography variant="body2SemiBold">Email Preferences</Typography>
            <Typography variant="body2" sx={{ mt: 1, color: 'text.light' }}>
              Manage your email notification preferences.
            </Typography>
          </Box>
          <Button
            variant="secondary"
            label="Edit"
            fullWidth={false}
            onClick={() => {
              navigate(Routes.EMAIL_PREFERENCES);
            }}
            sx={styles.button}
          />
        </Box>
      </Box>
      <LocalModal
        open={isAddRecoveryDialogOpen}
        onClose={() => setIsAddRecoveryDialogOpen(false)}
        name={'Add Recovery Email'}>
        <AddRecoveryEmail
          onSuccess={() => {
            setIsAddRecoveryDialogOpen(false);
          }}
          onCancel={() => {
            setIsAddRecoveryDialogOpen(false);
          }}
        />
      </LocalModal>
      <LocalModal
        open={isEditPasswordDialogOpen}
        onClose={() => setIsEditPasswordDialogOpen(false)}
        name={'Edit Password'}>
        <EditPassword
          onCancel={() => {
            setIsEditPasswordDialogOpen(false);
          }}
          onSuccess={() => {
            setIsEditPasswordDialogOpen(false);
          }}
        />
      </LocalModal>
      <LocalModal
        open={isEditEmailDialogOpen}
        onClose={() => setIsEditEmailDialogOpen(false)}
        name={'Edit Email'}>
        <EditEmail
          onSuccess={() => {
            setIsEditEmailDialogOpen(false);
          }}
          onCancel={() => {
            setIsEditEmailDialogOpen(false);
          }}
        />
      </LocalModal>
      <LocalModal
        open={isCancelSubDialogOpen}
        onClose={() => setIsCancelSubDialogOpen(false)}
        name={'Cancel Subscription'}>
        <CancelStripeSubscription
          planValidUntil={plan?.validUntil}
          onConfirm={onCancelSubscription}
          onCancel={() => setIsCancelSubDialogOpen(false)}
        />
      </LocalModal>
    </Box>
  );
};

const styles = {
  container: {
    borderTop: '1px solid #00000010',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    wordWrap: 'break-word',
  },
  button: {
    mt: 1,
    width: 50,
  },
};
